/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from 'data/api/apiClient';
import {
    DetailedUserDto,
    DetailedUsersListDto,
    DetailedUsersListQueryParams,
    NewUserData,
} from './usersClient.typings';
import { cutEmptyParams, toHash } from '../../../utils/utils';
import {
    isOperatorRights,
    isTaxInspectorRights,
} from '../../../screens/SystemScreens/AdminUsersListScreen/AdminUsersListUtils';

async function listDetailedUsers(params: DetailedUsersListQueryParams): Promise<DetailedUsersListDto> {
    return apiClient.get<DetailedUsersListDto>('1.0.0/users', prepareDetailedUsersListQueryParams(params));
}

async function getDetailedUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.get<DetailedUserDto>(`1.0.0/users/${userId}`);
}

async function updateDetailedUser(userId: string, data: Partial<NewUserData>): Promise<DetailedUserDto> {
    if (data.password) data.password = await toHash(data.password);
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}`, data);
}

async function changeUserPassword(userId: string, data: { oldPassword: string; newPassword: string }): Promise<void> {
    const hashedData = await Promise.all([await toHash(data.oldPassword), await toHash(data.newPassword)]);
    return apiClient.patch<void>(`1.0.0/users/${userId}/password`, {
        oldPassword: hashedData[0],
        newPassword: hashedData[1],
    });
}

async function blockUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}/block`);
}

async function unblockUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}/unblock`);
}

async function deleteUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.delete<DetailedUserDto>(`1.0.0/users/${userId}/delete`);
}

async function addUser(data: Partial<NewUserData>): Promise<DetailedUserDto> {
    data.password = await toHash(data.password);

    return apiClient.post<DetailedUserDto>('1.0.0/users/add', data);
}

async function downloadUsersAsExcel(params: DetailedUsersListQueryParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile('1.0.0/users/excel', prepareDetailedUsersListQueryParams(params));
}

const prepareDetailedUsersListQueryParams = (params: DetailedUsersListQueryParams): DetailedUsersListQueryParams => {
    const { search_type, search_text } = params;
    delete params.search_text;
    delete params.search_type;

    if (!isTaxInspectorRights([...params.uiType])) delete params.taxAuthorityId;

    if (!isOperatorRights([...params.uiType])) delete params.operatorTaxNumber;

    const queryParams = { ...params, [search_type]: search_text };
    return cutEmptyParams(queryParams);
};

export const usersClient = {
    listDetailedUsers,
    getDetailedUser,
    updateDetailedUser,
    changeUserPassword,
    blockUser,
    unblockUser,
    deleteUser,
    addUser,
    downloadUsersAsExcel,
};
